import React, { useState, useCallback } from 'react'
import { FolderKanban, Users, FileCheck, Clock, TrendingUp, AlertTriangle, CheckCircle, XCircle, ShieldAlert, ClipboardList } from 'lucide-react'
import { PageHeader } from '@components/common'
import {
	MetricCard,
	ReportCard,
	ReportTable,
	ReportFilter,
	ReportExport,
	BarChart,
	LineChart,
	DonutChart,
} from '@components/features/reports'
import { useReportData, useReportExport } from '@hooks'
import './Reports.scss'

/**
 * Company Reports Page
 *
 * Company-level analytics and reporting for administrators and managers.
 * Includes project metrics, team performance, compliance status, and SLA tracking.
 */
const Reports = () => {
	const [activeTab, setActiveTab] = useState('senior-management')

	// Filters state
	const [filters, setFilters] = useState({
		dateRange: { preset: 'last_30_days' },
	})

	// Report data hooks
	const {
		data: overviewData,
		loading: overviewLoading,
		refresh: refreshOverview,
	} = useReportData('company_overview', { initialFilters: filters })

	const {
		data: projectsData,
		loading: projectsLoading,
	} = useReportData('company_projects', { initialFilters: filters })

	const {
		data: teamData,
		loading: teamLoading,
	} = useReportData('company_team', { initialFilters: filters })

	const {
		data: slaData,
		loading: slaLoading,
	} = useReportData('company_sla', { initialFilters: filters })

	const {
		data: tasksData,
		loading: tasksLoading,
	} = useReportData('company_tasks', { initialFilters: filters })

	const {
		data: seniorMgmtData,
		loading: seniorMgmtLoading,
		refresh: refreshSeniorMgmt,
	} = useReportData('senior_management', { initialFilters: filters })

	const {
		data: cmData,
		loading: cmLoading,
		refresh: refreshCM,
	} = useReportData('compliance_manager', { initialFilters: filters })

	// Export hook
	const { exporting, exportReport } = useReportExport()

	// Handle filter change
	const handleFilterChange = useCallback((newFilters) => {
		setFilters(newFilters)
	}, [])

	// Handle filter apply
	const handleFilterApply = useCallback(() => {
		refreshOverview()
	}, [refreshOverview])

	// Handle export
	const handleExport = useCallback(async (format) => {
		await exportReport('company_overview', format, filters)
	}, [exportReport, filters])

	// Filter config
	const filterConfig = [
		{
			key: 'project_status',
			label: 'Project Status',
			type: 'select',
			options: [
				{ value: 'planning', label: 'Planning' },
				{ value: 'in_progress', label: 'In Progress' },
				{ value: 'review', label: 'Review' },
				{ value: 'completed', label: 'Completed' },
			],
		},
	]

	// Tabs
	const tabs = [
		{ id: 'senior-management', label: 'Senior Management', icon: <ShieldAlert size={16} /> },
		{ id: 'compliance-manager', label: 'Compliance Manager', icon: <ClipboardList size={16} /> },
		{ id: 'overview', label: 'Overview', icon: <TrendingUp size={16} /> },
		{ id: 'projects', label: 'Projects', icon: <FolderKanban size={16} /> },
		{ id: 'team', label: 'Team', icon: <Users size={16} /> },
		{ id: 'sla', label: 'SLA', icon: <Clock size={16} /> },
		{ id: 'tasks', label: 'Tasks', icon: <FileCheck size={16} /> },
	]

	return (
		<div className="company-reports">
			<PageHeader
				title="Company Reports"
				subtitle="Analytics and insights for your organization"
				actions={
					<ReportExport
						onExport={handleExport}
						loading={exporting}
					/>
				}
			/>

			{/* Filters */}
			<ReportFilter
				filters={filters}
				onChange={handleFilterChange}
				onApply={handleFilterApply}
				filterConfig={filterConfig}
				showDateRange={true}
			/>

			{/* Tabs */}
			<div className="reports-tabs">
				{tabs.map((tab) => (
					<button
						key={tab.id}
						className={`reports-tabs__tab ${activeTab === tab.id ? 'reports-tabs__tab--active' : ''}`}
						onClick={() => setActiveTab(tab.id)}
					>
						{tab.icon}
						<span>{tab.label}</span>
					</button>
				))}
			</div>

			{/* Tab Content */}
			<div className="reports-content">
				{activeTab === 'senior-management' && (
					<SeniorManagementTab
						data={seniorMgmtData}
						loading={seniorMgmtLoading}
						onRefresh={refreshSeniorMgmt}
					/>
				)}

				{activeTab === 'compliance-manager' && (
					<ComplianceManagerTab
						data={cmData}
						loading={cmLoading}
						onRefresh={refreshCM}
					/>
				)}

				{activeTab === 'overview' && (
					<OverviewTab
						data={overviewData}
						loading={overviewLoading}
						onRefresh={refreshOverview}
					/>
				)}

				{activeTab === 'projects' && (
					<ProjectsTab
						data={projectsData}
						loading={projectsLoading}
					/>
				)}

				{activeTab === 'team' && (
					<TeamTab
						data={teamData}
						loading={teamLoading}
					/>
				)}

				{activeTab === 'sla' && (
					<SLATab
						data={slaData}
						loading={slaLoading}
					/>
				)}

				{activeTab === 'tasks' && (
					<TasksTab
						data={tasksData}
						loading={tasksLoading}
					/>
				)}
			</div>
		</div>
	)
}

// Overview Tab Component
const OverviewTab = ({ data, loading, onRefresh }) => {
	const overview = data?.data || data || {}
	const summary = overview.summary || {}

	return (
		<div className="overview-tab">
			{/* Key Metrics */}
			<div className="metric-cards-grid">
				<MetricCard
					title="Total Projects"
					value={summary.total_projects || 0}
					subtitle={`${summary.active_projects || 0} active`}
					icon={<FolderKanban size={20} />}
					variant="primary"
					loading={loading}
				/>
				<MetricCard
					title="Total Tasks"
					value={summary.total_tasks || 0}
					subtitle={`${summary.completed_tasks || 0} completed`}
					icon={<FileCheck size={20} />}
					variant="success"
					loading={loading}
				/>
				<MetricCard
					title="Completion Rate"
					value={`${summary.task_completion_rate || 0}%`}
					icon={<TrendingUp size={20} />}
					variant="info"
					loading={loading}
				/>
				<MetricCard
					title="Overdue Tasks"
					value={summary.overdue_tasks || 0}
					icon={<AlertTriangle size={20} />}
					variant={summary.overdue_tasks > 0 ? 'danger' : 'success'}
					loading={loading}
				/>
			</div>

			{/* Metrics Charts */}
			{overview.metrics && (
				<ReportCard title="Key Metrics" loading={loading}>
					<div className="metrics-list">
						{overview.metrics.map((metric, index) => (
							<div key={index} className="metric-item">
								<span className="metric-item__label">{metric.label}</span>
								<span className="metric-item__value">{metric.value}</span>
							</div>
						))}
					</div>
				</ReportCard>
			)}
		</div>
	)
}

// Projects Tab Component
const ProjectsTab = ({ data, loading }) => {
	const projects = data?.data?.projects || data?.projects || data?.data || []

	const projectColumns = [
		{ key: 'name', label: 'Project', sortable: true },
		{ key: 'status', label: 'Status', sortable: true },
		{ key: 'manager_name', label: 'Manager', sortable: true },
		{ key: 'total_tasks', label: 'Tasks', sortable: true, align: 'right' },
		{ key: 'completed_tasks', label: 'Completed', sortable: true, align: 'right' },
		{ key: 'calculated_progress', label: 'Progress', sortable: true, align: 'right', format: 'percent' },
		{ key: 'health', label: 'Health', sortable: true },
	]

	// Calculate status distribution
	const statusCounts = projects.reduce((acc, p) => {
		acc[p.status] = (acc[p.status] || 0) + 1
		return acc
	}, {})

	return (
		<div className="projects-tab">
			<div className="reports-grid reports-grid--2">
				<ReportCard title="Project Status Distribution" loading={loading}>
					<DonutChart
						data={Object.entries(statusCounts).map(([status, count]) => ({
							label: status.replace('_', ' '),
							value: count,
						}))}
						showLegend={true}
						size={200}
					/>
				</ReportCard>

				<ReportCard title="Project Health Overview" loading={loading}>
					<DonutChart
						data={[
							{ label: 'On Track', value: projects.filter((p) => p.health === 'on_track').length, color: '#10b981' },
							{ label: 'At Risk', value: projects.filter((p) => p.health === 'at_risk').length, color: '#f59e0b' },
							{ label: 'Delayed', value: projects.filter((p) => p.health === 'delayed').length, color: '#ef4444' },
						]}
						showLegend={true}
						size={200}
					/>
				</ReportCard>
			</div>

			<ReportCard title="All Projects" loading={loading}>
				<ReportTable
					columns={projectColumns}
					data={projects}
					loading={loading}
					searchable={true}
					exportable={true}
					emptyMessage="No projects found"
				/>
			</ReportCard>
		</div>
	)
}

// Team Tab Component
const TeamTab = ({ data, loading }) => {
	const team = data?.data || data || {}
	const summary = team.summary || {}
	const userPerformance = team.user_performance || []
	const workloadDistribution = team.workload_distribution || []

	const performanceColumns = [
		{ key: 'user_name', label: 'Team Member', sortable: true },
		{ key: 'role_name', label: 'Role', sortable: true },
		{ key: 'assigned', label: 'Assigned', sortable: true, align: 'right' },
		{ key: 'completed', label: 'Completed', sortable: true, align: 'right' },
		{ key: 'in_progress', label: 'In Progress', sortable: true, align: 'right' },
		{ key: 'completion_rate', label: 'Rate', sortable: true, align: 'right', format: 'percent' },
	]

	return (
		<div className="team-tab">
			<div className="metric-cards-grid">
				<MetricCard
					title="Team Members"
					value={summary.total_members || 0}
					icon={<Users size={20} />}
					variant="primary"
					loading={loading}
				/>
				<MetricCard
					title="Active Members"
					value={summary.active_members || 0}
					icon={<CheckCircle size={20} />}
					variant="success"
					loading={loading}
				/>
			</div>

			<div className="reports-grid reports-grid--2">
				<ReportCard title="Workload Distribution" loading={loading}>
					<BarChart
						data={workloadDistribution.map((w) => ({
							label: w.user_name,
							value: w.task_count,
						}))}
						horizontal={true}
					/>
				</ReportCard>

				<ReportCard title="Performance by Role" loading={loading}>
					{team.performance_by_role && (
						<BarChart
							data={team.performance_by_role.map((r) => ({
								label: r.role_name,
								value: parseFloat(r.avg_completion_rate) || 0,
							}))}
							formatValue={(v) => `${v.toFixed(0)}%`}
						/>
					)}
				</ReportCard>
			</div>

			<ReportCard title="Team Performance" loading={loading}>
				<ReportTable
					columns={performanceColumns}
					data={userPerformance}
					loading={loading}
					searchable={true}
					exportable={true}
					emptyMessage="No team data available"
				/>
			</ReportCard>
		</div>
	)
}

// SLA Tab Component
const SLATab = ({ data, loading }) => {
	const sla = data?.data || data || {}
	const summary = sla.summary || {}
	const byWorkflow = sla.by_workflow || []
	const breaches = sla.breaches || []

	const breachColumns = [
		{ key: 'checklist_title', label: 'Task', sortable: true },
		{ key: 'project_name', label: 'Project', sortable: true },
		{ key: 'step_name', label: 'Step', sortable: true },
		{ key: 'sla_hours', label: 'SLA (hrs)', sortable: true, align: 'right' },
		{ key: 'actual_hours', label: 'Actual (hrs)', sortable: true, align: 'right' },
		{ key: 'breach_hours', label: 'Breach (hrs)', sortable: true, align: 'right' },
	]

	return (
		<div className="sla-tab">
			<div className="metric-cards-grid">
				<MetricCard
					title="SLA Compliance Rate"
					value={`${summary.compliance_rate || 0}%`}
					icon={<Clock size={20} />}
					variant={summary.compliance_rate >= 90 ? 'success' : summary.compliance_rate >= 70 ? 'warning' : 'danger'}
					loading={loading}
				/>
				<MetricCard
					title="Total SLA Tracked"
					value={summary.total_tracked || 0}
					icon={<FileCheck size={20} />}
					variant="info"
					loading={loading}
				/>
				<MetricCard
					title="Met SLA"
					value={summary.met_sla || 0}
					icon={<CheckCircle size={20} />}
					variant="success"
					loading={loading}
				/>
				<MetricCard
					title="Breached SLA"
					value={summary.breached_sla || 0}
					icon={<XCircle size={20} />}
					variant="danger"
					loading={loading}
				/>
			</div>

			<div className="reports-grid reports-grid--2">
				<ReportCard title="SLA by Workflow" loading={loading}>
					<BarChart
						data={byWorkflow.map((w) => ({
							label: w.workflow_name,
							value: parseFloat(w.compliance_rate) || 0,
						}))}
						formatValue={(v) => `${v.toFixed(0)}%`}
						color="var(--color-success)"
					/>
				</ReportCard>

				<ReportCard title="SLA Compliance Trend" loading={loading}>
					{sla.trend && (
						<LineChart
							data={sla.trend}
							valueKey="compliance_rate"
							showArea={true}
							color="var(--color-primary)"
						/>
					)}
				</ReportCard>
			</div>

			{breaches.length > 0 && (
				<ReportCard title="SLA Breaches" loading={loading}>
					<ReportTable
						columns={breachColumns}
						data={breaches}
						loading={loading}
						searchable={true}
						striped={true}
						emptyMessage="No SLA breaches"
					/>
				</ReportCard>
			)}
		</div>
	)
}

// Tasks Tab Component
const TasksTab = ({ data, loading }) => {
	const tasks = data?.data || data || {}
	const summary = tasks.summary || {}
	const byStatus = tasks.by_status || summary.by_status || {}
	const taskList = tasks.tasks || []
	const overdue = tasks.overdue || []

	const taskColumns = [
		{ key: 'title', label: 'Task', sortable: true },
		{ key: 'project_name', label: 'Project', sortable: true },
		{ key: 'workflow_status', label: 'Status', sortable: true },
		{ key: 'assignee_name', label: 'Assignee', sortable: true },
		{ key: 'due_date', label: 'Due Date', sortable: true, format: 'date' },
	]

	return (
		<div className="tasks-tab">
			<div className="metric-cards-grid">
				<MetricCard
					title="Total Tasks"
					value={summary.total || 0}
					icon={<FileCheck size={20} />}
					variant="primary"
					loading={loading}
				/>
				<MetricCard
					title="Completed"
					value={byStatus.completed || 0}
					icon={<CheckCircle size={20} />}
					variant="success"
					loading={loading}
				/>
				<MetricCard
					title="In Progress"
					value={byStatus.in_progress || 0}
					icon={<TrendingUp size={20} />}
					variant="info"
					loading={loading}
				/>
				<MetricCard
					title="Overdue"
					value={summary.overdue || 0}
					icon={<AlertTriangle size={20} />}
					variant="danger"
					loading={loading}
				/>
			</div>

			<div className="reports-grid reports-grid--2">
				<ReportCard title="Task Status Distribution" loading={loading}>
					<DonutChart
						data={Object.entries(byStatus).map(([status, count]) => ({
							label: status.replace('_', ' '),
							value: count,
						}))}
						showLegend={true}
						size={200}
					/>
				</ReportCard>

				<ReportCard title="Task Aging" loading={loading}>
					{tasks.aging && (
						<BarChart
							data={tasks.aging.buckets?.map((b) => ({
								label: b.range,
								value: b.count,
								color: b.color,
							})) || []}
						/>
					)}
				</ReportCard>
			</div>

			{overdue.length > 0 && (
				<ReportCard title="Overdue Tasks" loading={loading}>
					<ReportTable
						columns={[
							...taskColumns,
							{ key: 'days_overdue', label: 'Days Overdue', sortable: true, align: 'right' },
						]}
						data={overdue}
						loading={loading}
						striped={true}
						emptyMessage="No overdue tasks"
					/>
				</ReportCard>
			)}
		</div>
	)
}

// ─────────────────────────────────────────────────────────────────────────────
// Compliance Manager Dashboard Tab
// ─────────────────────────────────────────────────────────────────────────────

/** Priority label → MetricCard variant */
const priorityVariant = (p) =>
	({ immediate: 'danger', high: 'danger', medium: 'warning', low: 'success' })[p] || 'secondary'

/** Urgency string → badge CSS modifier */
const urgencyClass = (u) =>
	({ overdue: 'danger', critical: 'danger', high: 'warning', medium: 'info' })[u] || 'secondary'

const ComplianceManagerTab = ({ data, loading, onRefresh }) => {
	const report   = data?.data || data || {}
	const overview = report.personal_overview        || {}
	const overdue  = report.tasks_overdue            || []
	const dueWeek  = report.tasks_due_this_week      || []
	const risks    = report.open_risks               || []
	const evidence = report.evidence_expiry          || []
	const exc      = report.exceptions               || {}
	const srcDocs  = report.source_doc_verification  || {}
	const actions  = report.action_summary           || []
	const riskScore = overview.risk_score || {}

	// ── Table column definitions ──────────────────────────────────────────────

	const overdueColumns = [
		{ key: 'task_name',    label: 'Task Name',   sortable: true },
		{ key: 'owner',        label: 'Owner',        sortable: true },
		{ key: 'due_date',     label: 'Due Date',     sortable: true, format: 'date' },
		{ key: 'days_overdue', label: 'Days Overdue', sortable: true, align: 'right' },
		{ key: 'status',       label: 'Status',       sortable: true },
		{ key: 'project_name', label: 'Project',      sortable: true },
	]

	const dueWeekColumns = [
		{ key: 'task_name',    label: 'Task Name',  sortable: true },
		{ key: 'owner',        label: 'Owner',       sortable: true },
		{ key: 'due_date',     label: 'Due Date',    sortable: true, format: 'date' },
		{ key: 'days_until',   label: 'Days Left',   sortable: true, align: 'right' },
		{ key: 'project_name', label: 'Project',     sortable: true },
	]

	const riskColumns = [
		{ key: 'risk_name',    label: 'Risk Name',  sortable: true },
		{ key: 'severity',     label: 'Severity',   sortable: true },
		{ key: 'owner',        label: 'Owner',       sortable: true },
		{ key: 'status',       label: 'Status',      sortable: true },
		{ key: 'age_days',     label: 'Age (days)',  sortable: true, align: 'right' },
		{ key: 'project_name', label: 'Project',     sortable: true },
	]

	const evidenceColumns = [
		{ key: 'evidence_name',  label: 'Evidence / Task',  sortable: true },
		{ key: 'expiry_date',    label: 'Due Date',          sortable: true, format: 'date' },
		{ key: 'days_remaining', label: 'Days Remaining',    sortable: true, align: 'right' },
		{ key: 'linked_control', label: 'Linked Control',    sortable: true },
		{ key: 'project_name',   label: 'Project',           sortable: true },
	]

	const srcDocColumns = [
		{ key: 'document_name',       label: 'Document',         sortable: true },
		{ key: 'document_type',       label: 'Type',             sortable: true },
		{ key: 'last_verified',       label: 'Last Updated',     sortable: true, format: 'date' },
		{ key: 'days_since_verified', label: 'Days Since Update', sortable: true, align: 'right' },
		{ key: 'project_name',        label: 'Project',          sortable: true },
	]

	return (
		<div className="compliance-manager-tab">

			{/* ── Section 1: Personal Compliance Overview ── */}
			<div className="sm-section-title">My Compliance Overview</div>
			<div className="metric-cards-grid metric-cards-grid--4">
				<MetricCard
					title="My Readiness %"
					value={`${overview.readiness_pct ?? 0}%`}
					icon={<CheckCircle size={20} />}
					variant={
						(overview.readiness_pct ?? 0) >= 80 ? 'success'
						: (overview.readiness_pct ?? 0) >= 60 ? 'warning'
						: 'danger'
					}
					loading={loading}
				/>
				<MetricCard
					title={`My Risk Score — ${riskScore.level || '—'}`}
					value={riskScore.score ?? '—'}
					subtitle={`C:${riskScore.critical ?? 0}  H:${riskScore.high ?? 0}  M:${riskScore.medium ?? 0}  L:${riskScore.low ?? 0}`}
					icon={<ShieldAlert size={20} />}
					variant={riskLevelClass(riskScore.level)}
					loading={loading}
				/>
				<MetricCard
					title="Overdue Items"
					value={overview.overdue_items ?? 0}
					icon={<Clock size={20} />}
					variant={(overview.overdue_items ?? 0) > 0 ? 'danger' : 'success'}
					loading={loading}
				/>
				<MetricCard
					title="Incidents (Exceptions)"
					value={overview.incidents ?? 0}
					icon={<AlertTriangle size={20} />}
					variant={(overview.incidents ?? 0) > 0 ? 'warning' : 'success'}
					loading={loading}
				/>
			</div>

			{/* ── Section 8: Action Summary (shown near top for visibility) ── */}
			{actions.length > 0 && (
				<ReportCard title="Action Summary" loading={loading}>
					<ul className="cm-action-list">
						{actions.map((a, i) => (
							<li key={i} className={`cm-action-list__item cm-action-list__item--${priorityVariant(a.priority)}`}>
								{a.text}
							</li>
						))}
					</ul>
				</ReportCard>
			)}

			{/* ── Sections 2 & 3: Overdue + Due This Week ── */}
			<div className="reports-grid reports-grid--2">
				<ReportCard
					title={`Tasks Overdue — My Scope (${overdue.length})`}
					loading={loading}
				>
					<ReportTable
						columns={overdueColumns}
						data={overdue}
						loading={loading}
						striped={true}
						searchable={false}
						emptyMessage="No overdue tasks — great work!"
					/>
				</ReportCard>

				<ReportCard
					title={`Tasks Due This Week (${dueWeek.length})`}
					loading={loading}
				>
					<ReportTable
						columns={dueWeekColumns}
						data={dueWeek}
						loading={loading}
						striped={true}
						searchable={false}
						emptyMessage="No tasks due in the next 7 days."
					/>
				</ReportCard>
			</div>

			{/* ── Section 4: Open Risks ── */}
			<ReportCard title={`Open Risks — My Scope (${risks.length})`} loading={loading}>
				<ReportTable
					columns={riskColumns}
					data={risks}
					loading={loading}
					searchable={true}
					emptyMessage="No open risks in your scope."
				/>
			</ReportCard>

			{/* ── Section 5: Evidence Expiry ── */}
			<ReportCard title={`Evidence Expiry Alerts (${evidence.length})`} loading={loading}>
				{evidence.length === 0 ? (
					<p className="sm-empty">All evidence submissions are on track.</p>
				) : (
					<>
						<div className="cm-urgency-legend">
							<span className="cm-badge cm-badge--danger">OVERDUE / CRITICAL ≤ 7 days</span>
							<span className="cm-badge cm-badge--warning">HIGH 8–15 days</span>
							<span className="cm-badge cm-badge--info">MEDIUM 16–30 days</span>
						</div>
						<ReportTable
							columns={evidenceColumns}
							data={evidence}
							loading={loading}
							searchable={false}
							emptyMessage="No evidence expiry alerts."
						/>
					</>
				)}
			</ReportCard>

			{/* ── Sections 6 & 7: Exceptions + Source Docs ── */}
			<div className="reports-grid reports-grid--2">
				<ReportCard title="Exceptions Management" loading={loading}>
					<div className="cm-exceptions">
						<div className="cm-exceptions__row">
							<MetricCard
								title="Pending Approval"
								value={exc.pending_approval ?? 0}
								icon={<XCircle size={20} />}
								variant={(exc.pending_approval ?? 0) > 0 ? 'danger' : 'success'}
								loading={loading}
							/>
							<MetricCard
								title="Total Raised"
								value={exc.total_raised ?? 0}
								icon={<AlertTriangle size={20} />}
								variant={(exc.total_raised ?? 0) > 0 ? 'warning' : 'success'}
								loading={loading}
							/>
						</div>
						{exc.urgency_note && (
							<p className={`cm-exceptions__note cm-exceptions__note--${(exc.pending_approval ?? 0) > 0 ? 'warning' : 'success'}`}>
								{exc.urgency_note}
							</p>
						)}
					</div>
				</ReportCard>

				<ReportCard title="Source Document Verification Pending" loading={loading}>
					<div className="cm-src-docs">
						<div className="cm-src-docs__count">
							<span className="cm-src-docs__number">{srcDocs.total ?? 0}</span>
							<span className="cm-src-docs__label">documents require verification</span>
						</div>
						{srcDocs.note && (
							<p className={`cm-exceptions__note cm-exceptions__note--${(srcDocs.total ?? 0) > 0 ? 'warning' : 'success'}`}>
								{srcDocs.note}
							</p>
						)}
					</div>
				</ReportCard>
			</div>

			{/* Source doc detail table */}
			{(srcDocs.items || []).length > 0 && (
				<ReportCard title="Documents Pending Verification (Top 10)" loading={loading}>
					<ReportTable
						columns={srcDocColumns}
						data={srcDocs.items || []}
						loading={loading}
						searchable={false}
						emptyMessage="No documents pending verification."
					/>
				</ReportCard>
			)}
		</div>
	)
}

// ─────────────────────────────────────────────────────────────────────────────
// Senior Management Compliance Dashboard Tab
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Colour helper: maps risk_level string → CSS class suffix used in SCSS.
 */
const riskLevelClass = (level) => {
	const map = { High: 'danger', Medium: 'warning', Low: 'success' }
	return map[level] || 'secondary'
}

/**
 * Colour helper: severity → CSS class suffix.
 */
const severityClass = (sev) => {
	const map = { Critical: 'danger', High: 'warning', Medium: 'info', Low: 'success' }
	return map[sev] || 'secondary'
}

const SeniorManagementTab = ({ data, loading, onRefresh }) => {
	const report   = data?.data || data || {}
	const overview = report.compliance_overview   || {}
	const depts    = report.risk_by_department    || []
	const projects = report.risk_by_project       || []
	const risks    = report.top_critical_risks     || []
	const incidents= report.open_incidents         || {}
	const bcp      = report.bcp_readiness          || {}
	const overdue  = report.overdue_actions        || {}
	const exceptions = report.exceptions_pending   || {}
	const evidence = report.evidence_health        || {}
	const trend    = report.compliance_trend       || []

	const riskScore = overview.risk_score || {}

	// Incident bar-chart data
	const incidentChartData = Object.entries(incidents.by_severity || {}).map(([sev, count]) => ({
		label: sev,
		value: count,
	}))

	// Compliance trend chart lines
	const trendLines = [{ dataKey: 'score', name: 'Compliance Score', color: 'var(--color-primary)' }]

	// Top-5 critical risks table columns
	const riskColumns = [
		{ key: 'risk_name',    label: 'Risk Name',  sortable: true },
		{ key: 'severity',     label: 'Severity',   sortable: true },
		{ key: 'owner',        label: 'Owner',      sortable: true },
		{ key: 'status',       label: 'Status',     sortable: true },
		{ key: 'age_days',     label: 'Age (days)', sortable: true, align: 'right' },
		{ key: 'project_name', label: 'Project',    sortable: true },
	]

	// Top-5 projects table columns
	const projectRiskColumns = [
		{ key: 'project_name',   label: 'Project',     sortable: true },
		{ key: 'risk_score',     label: 'Risk Score',  sortable: true, align: 'right' },
		{ key: 'risk_level',     label: 'Risk Level',  sortable: true },
		{ key: 'project_status', label: 'Status',      sortable: true },
	]

	return (
		<div className="senior-management-tab">

			{/* ── Section 1: Compliance Overview ── */}
			<div className="sm-section-title">Compliance Overview</div>
			<div className="metric-cards-grid metric-cards-grid--4">
				<MetricCard
					title="Compliance Readiness"
					value={`${overview.compliance_readiness_pct ?? 0}%`}
					icon={<CheckCircle size={20} />}
					variant={
						(overview.compliance_readiness_pct ?? 0) >= 80 ? 'success'
						: (overview.compliance_readiness_pct ?? 0) >= 60 ? 'warning'
						: 'danger'
					}
					loading={loading}
				/>
				<MetricCard
					title={`Risk Score — ${riskScore.level || '—'}`}
					value={riskScore.score ?? '—'}
					subtitle={`C:${riskScore.critical ?? 0}  H:${riskScore.high ?? 0}  M:${riskScore.medium ?? 0}  L:${riskScore.low ?? 0}`}
					icon={<ShieldAlert size={20} />}
					variant={riskLevelClass(riskScore.level)}
					loading={loading}
				/>
				<MetricCard
					title="Overdue Items"
					value={overview.overdue_items ?? 0}
					icon={<Clock size={20} />}
					variant={(overview.overdue_items ?? 0) > 0 ? 'danger' : 'success'}
					loading={loading}
				/>
				<MetricCard
					title="Open Incidents"
					value={overview.incidents ?? 0}
					icon={<AlertTriangle size={20} />}
					variant={(overview.incidents ?? 0) > 0 ? 'warning' : 'success'}
					loading={loading}
				/>
			</div>

			{/* ── Section 2: Risk Score by Department ── */}
			<ReportCard title="Risk Score by Department / Practice" loading={loading}>
				{depts.length === 0 ? (
					<p className="sm-empty">No department risk data available.</p>
				) : (
					<div className="sm-heatmap">
						{depts.map((d) => (
							<div key={d.department} className={`sm-heatmap__cell sm-heatmap__cell--${riskLevelClass(d.risk_level)}`}>
								<span className="sm-heatmap__dept">{d.department}</span>
								<span className="sm-heatmap__score">{d.risk_score}</span>
								<span className="sm-heatmap__level">{d.risk_level}</span>
							</div>
						))}
					</div>
				)}
			</ReportCard>

			{/* ── Sections 3 & 4: Projects + Critical Risks ── */}
			<div className="reports-grid reports-grid--2">
				<ReportCard title="Risk Score by Project (Top 5)" loading={loading}>
					<ReportTable
						columns={projectRiskColumns}
						data={projects}
						loading={loading}
						emptyMessage="No project risk data available"
					/>
				</ReportCard>

				<ReportCard title="Top 5 Critical Risks" loading={loading}>
					<ReportTable
						columns={riskColumns}
						data={risks}
						loading={loading}
						emptyMessage="No critical risks found"
					/>
				</ReportCard>
			</div>

			{/* ── Section 5: Open Incidents by Severity ── */}
			<ReportCard title="Open Incidents by Severity" loading={loading}>
				<div className="sm-incidents-row">
					<div className="sm-incidents-chart">
						<BarChart
							data={incidentChartData}
							color="var(--color-warning)"
						/>
					</div>
					<div className="sm-incidents-total">
						<span className="sm-incidents-total__label">Total Open</span>
						<span className="sm-incidents-total__value">{incidents.total ?? 0}</span>
					</div>
				</div>
			</ReportCard>

			{/* ── Sections 6-9: Operational Metrics ── */}
			<div className="sm-section-title">Operational Alerts</div>
			<div className="metric-cards-grid metric-cards-grid--4">
				<MetricCard
					title="BCP Readiness"
					value={`${bcp.readiness_pct ?? 0}%`}
					subtitle={`${bcp.completed ?? 0} / ${bcp.total ?? 0} tasks`}
					icon={<FileCheck size={20} />}
					variant={(bcp.readiness_pct ?? 0) >= 80 ? 'success' : (bcp.readiness_pct ?? 0) >= 50 ? 'warning' : 'danger'}
					loading={loading}
				/>
				<MetricCard
					title="Overdue Actions (Org)"
					value={overdue.total ?? 0}
					subtitle={`Critical: ${overdue.critical ?? 0}  High: ${overdue.high ?? 0}`}
					icon={<AlertTriangle size={20} />}
					variant={(overdue.total ?? 0) > 0 ? 'danger' : 'success'}
					loading={loading}
				/>
				<MetricCard
					title="Exceptions Pending Approval"
					value={exceptions.count ?? 0}
					icon={<XCircle size={20} />}
					variant={(exceptions.count ?? 0) > 0 ? 'warning' : 'success'}
					loading={loading}
				/>
				<MetricCard
					title="Evidence Health"
					value={`${evidence.health_pct ?? 0}%`}
					subtitle={`${evidence.missing_evidence ?? 0} tasks missing evidence`}
					icon={<CheckCircle size={20} />}
					variant={(evidence.health_pct ?? 0) >= 80 ? 'success' : (evidence.health_pct ?? 0) >= 50 ? 'warning' : 'danger'}
					loading={loading}
				/>
			</div>

			{/* ── Optional: Exceptions detail table ── */}
			{(exceptions.items || []).length > 0 && (
				<ReportCard title="Exceptions Pending Executive Decision" loading={loading}>
					<ReportTable
						columns={[
							{ key: 'title',         label: 'Item',       sortable: true },
							{ key: 'project_name',  label: 'Project',    sortable: true },
							{ key: 'excused_by',    label: 'Excused By', sortable: true },
							{ key: 'excuse_reason', label: 'Reason',     sortable: false },
							{ key: 'excused_at',    label: 'Date',       sortable: true, format: 'date' },
						]}
						data={exceptions.items || []}
						loading={loading}
						emptyMessage="No pending exceptions"
					/>
				</ReportCard>
			)}

			{/* ── Section 11 (Optional): Compliance Trend ── */}
			{trend.length > 0 && (
				<ReportCard title="Compliance Trend (Last 30 Days)" loading={loading}>
					<LineChart
						data={trend}
						valueKey="score"
						showArea={true}
						color="var(--color-primary)"
					/>
				</ReportCard>
			)}
		</div>
	)
}

export default Reports
